/**
 * @fileoverview The admin's "view as a gardener" picker (card E16).
 *
 * A list of gardeners with a "View as" button each. Pressing it keeps that
 * gardener for this tab, empties the caches and opens Today as they see it,
 * read-only (shared/api/viewAs). Only admins are offered the page; the server
 * refuses the list to anyone else and its refusal is said here.
 *
 * The server's Gardeners list (Grow settings) links here with
 * /admin/view-as?start=<userId>: the page checks the admin list call succeeds,
 * finds that gardener (digits match the user id first), and starts the view as
 * them. A non-admin gets the list call's normal refusal, shown here, and
 * nothing starts.
 *
 * Where: the route /admin/view-as (AppRoutes), linked from the header's menu
 * for Grow admins (the server's GROW_ADMIN_EMAILS).
 * API: GET /admin/grow-oversight/gardeners?q= (shared/api/adminUsers).
 * Words (en/es): viewAs.*.
 *
 * @module ViewAsPage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { getAdminUsers } from '@/shared/api/adminUsers';
import { startViewAs } from '@/shared/api/viewAs';

/** The picker: a row per gardener with name, email, place and the View as button. */
export const ViewAsPage: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const [params] = useSearchParams();
  const startParam = params.get('start') ?? '';
  const startId = /^\d+$/.test(startParam) ? Number(startParam) : null;
  const [search, setSearch] = React.useState('');
  const [q, setQ] = React.useState(startId === null ? '' : String(startId));
  const { data: users = [], isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'users', q],
    queryFn: () => getAdminUsers(q),
  });

  // Arrived from the Gardeners list's "View as" link: start the view once that gardener is found.
  React.useEffect(() => {
    if (startId === null) return;
    const who = users.find((user) => user.userId === startId);
    if (who) startViewAs({ id: who.userId, name: who.name });
  }, [startId, users]);

  return (
    <div className="p-4">
      <Link to="/plants" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('viewAs.back')}
      </Link>
      <h1 className="font-body text-2xl font-semibold">{t('viewAs.title')}</h1>
      <p className="mb-4 mt-1 max-w-prose text-sm text-muted-foreground">{t('viewAs.intro')}</p>
      <form className="mb-4 flex max-w-md gap-2" onSubmit={(e) => { e.preventDefault(); setQ(search.trim()); }}>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('viewAs.search')} aria-label={t('viewAs.search')} />
        <Button type="submit" variant="secondary">{t('viewAs.searchButton')}</Button>
      </form>
      {isError ? (
        <p role="alert" className="py-8 text-sm text-destructive"><FailureText text={errorMessage(error, t('viewAs.loadFailed'))} /></p>
      ) : isLoading ? (
        <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground"><Spinner size={20} />{t('viewAs.loading')}</div>
      ) : users.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">{t('viewAs.empty')}</p>
      ) : (
        <ul className="divide-y rounded-sm border bg-card">
          {users.map((user) => (
            <li key={user.userId} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{user.name}</p>
                <p className="truncate text-xs text-muted-foreground">{[user.email, user.place].filter(Boolean).join(' · ')}</p>
              </div>
              <Button size="sm" onClick={() => startViewAs({ id: user.userId, name: user.name })}>{t('viewAs.button')}</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ViewAsPage;
